import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useCurrentUser } from "@/lib/auth";
import {
  DECO_CHANGE_EVENT,
  applyDecoPreferenceLocally,
  fetchDecoPreferenceFromServer,
  loadDecoPreferenceLocal,
  saveDecoPreference,
  type DecoPreference,
} from "@/lib/decoPreference";

interface DecoPreferenceContextValue {
  preference: DecoPreference;
  setPreference: (next: DecoPreference) => void;
  loading: boolean;
}

const DecoPreferenceContext = createContext<DecoPreferenceContextValue | null>(null);

export function DecoPreferenceProvider({ children }: { children: ReactNode }) {
  const { user } = useCurrentUser();
  const [preference, setPreferenceState] = useState<DecoPreference>(() => loadDecoPreferenceLocal());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void fetchDecoPreferenceFromServer()
      .then((pref) => {
        if (cancelled) return;
        setPreferenceState(pref);
        applyDecoPreferenceLocally(pref);
      })
      .catch(() => {
        if (cancelled) return;
        setPreferenceState(loadDecoPreferenceLocal());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{ preference?: DecoPreference }>).detail;
      if (detail?.preference) {
        setPreferenceState(detail.preference);
        return;
      }
      setPreferenceState(loadDecoPreferenceLocal());
    };

    window.addEventListener(DECO_CHANGE_EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(DECO_CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const setPreference = useCallback((next: DecoPreference) => {
    setPreferenceState(next);
    void saveDecoPreference(next);
  }, []);

  const value = useMemo(
    () => ({ preference, setPreference, loading }),
    [preference, setPreference, loading],
  );

  return <DecoPreferenceContext.Provider value={value}>{children}</DecoPreferenceContext.Provider>;
}

export function useDecoPreference(): DecoPreferenceContextValue {
  const ctx = useContext(DecoPreferenceContext);
  if (!ctx) {
    return {
      preference: loadDecoPreferenceLocal(),
      setPreference: (next) => {
        void saveDecoPreference(next);
      },
      loading: false,
    };
  }
  return ctx;
}
